import { type ReactNode } from 'react';
import { darken, lighten, rgba, saturate } from 'polished';
import styled, { css } from 'styled-components';

import { generateFluidPxScale } from '../blog-2/utils';
import { LinkWithQuery } from '../global/LinkWithQuery';
import { generateFluidFontSize } from '../v2/UtilityCss/utils';

export const roseGlassCss = css`
  background: linear-gradient(
      155deg,
      rgba(0 0 0 / 0),
      rgba(0 0 0 / 0) 6%,
      rgba(0 0 0 / 0.24)
    ),
    ${({ theme }) => theme.palette.fgA700};
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);

  box-shadow: 0 4px 8px -1px rgba(0 0 0 / 0.64);
  transition: filter 0.3s;

  &:hover {
    filter: brightness(1.13);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${({ theme }) => {
      const stop1 = theme.palette.fgA1000;
      const stop2 = theme.palette.fgA800;
      return `linear-gradient(155deg, rgba(255 255 255 / 0.16), ${stop1} 15%, ${stop2} 25%)`;
    }};
    border: 1px solid transparent;

    mask:
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px)
        padding-box exclude,
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px);
  }
`;

export const PrimaryCta = styled(LinkWithQuery)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 14px 22px 14px 14px;
  padding: ${generateFluidPxScale(8, 14)} ${generateFluidPxScale(18, 22)}
    ${generateFluidPxScale(8, 14)} ${generateFluidPxScale(6, 14)};
  gap: 16px;
  color: ${({ theme }) => theme.palette.fg1200};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  white-space: nowrap;

  border: 0;
  border-radius: 1px;
  font-size: ${generateFluidFontSize(16, 20, {
    minWidth: 800,
    maxWidth: 1708
  })};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  font-family: inherit;
  letter-spacing: 0.25px;
  user-select: none;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);
  border: 1px solid transparent;
  box-shadow: 0 4px 8px -1px rgba(0 0 0 / 0.64);

  &[data-no-icon] {
    padding: ${generateFluidPxScale(8, 14)} ${generateFluidPxScale(18, 22)};
  }

  ${({ theme }) => theme.buttonLoudStyles};

  &::after {
    content: '';
    pointer-events: none;
    position: absolute;
    inset: 0;
    border-radius: inherit;
    z-index: 1;
    border: 1px solid transparent;
    opacity: 0.2;

    background-clip: padding-box, border-box;
    background-origin: padding-box, border-box;
    transition: ${({ theme }) =>
      `opacity ${theme.animation.DURATION} ${theme.animation.EASING}`};
  }

  &::before {
    border-radius: inherit;
  }

  &:hover::after {
    opacity: 0.3;
  }

  &:active::after {
    opacity: 0.6;
  }

  background: ${({ theme }) => {
    return `linear-gradient(
      to right,
      ${theme.backgroundColor},
      ${theme.backgroundColor}
    ),
    linear-gradient(
      45deg,
      ${theme.highlightColor},
      ${theme.colors.LIGHT_PURPLE}
    )`;
  }};
  background-clip: padding-box, border-box;
  background-origin: padding-box, border-box;
`;

export const PrimaryCtaNew = styled(LinkWithQuery)`
  position: relative;
  display: flex;
  align-items: center;
  padding: 14px 22px 14px 14px;
  padding: ${generateFluidPxScale(8, 14)} ${generateFluidPxScale(18, 22)}
    ${generateFluidPxScale(8, 14)} ${generateFluidPxScale(6, 14)};
  gap: 16px;
  color: ${({ theme }) => theme.palette.fg1200};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  white-space: nowrap;

  border: 0;
  border-radius: 1px;
  font-size: ${generateFluidFontSize(16, 20, {
    minWidth: 800,
    maxWidth: 1708
  })};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  font-family: inherit;
  letter-spacing: 0.25px;
  user-select: none;
  text-shadow: 0px 1px 1px rgba(0, 0, 0, 0.16);

  ${roseGlassCss}
`;

export const PrimaryCtaIconWrapper = styled.div`
  position: relative;
  display: grid;
  place-content: center;
  aspect-ratio: 1 / 1;
  color: ${({ theme }) => theme.palette.fg1200};
  border: 1px solid ${({ theme }) => theme.palette.fg1200};
  border-radius: 999px;
  width: 36px;

  &:hover {
    filter: lighten(0.1);
  }
`;

export const PrimaryCtaIconWrapperNew = styled.div`
  position: relative;
  display: grid;
  place-content: center;
  aspect-ratio: 1 / 1;
  color: ${({ theme }) => theme.palette.fg1200};
  border-radius: 999px;
  width: 36px;

  background-image: ${({ theme }) => {
    const stop1 = rgba(theme.palette.fg1200, 0.13);
    const stop2 = rgba('#FFDFE2', 0.13);
    return `linear-gradient(to right, ${stop1}, ${stop2})`;
  }};

  box-shadow: 1px 2px 5px 1px rgba(0 0 0 / 0.64) inset;

  &:hover {
    filter: lighten(0.1);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    background: ${({ theme }) => {
      const stop1 = theme.palette.fg1000;
      const stop2 = theme.palette.fg800;
      return `linear-gradient(-25deg, ${stop1}, ${stop1} 35%, ${stop2} 55%, ${stop2}) border-box border-box`;
    }};
    background-size: cover;
    border: 1px solid transparent;

    mask:
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px)
        padding-box exclude,
      linear-gradient(rgb(255, 255, 255) 0px, rgb(255, 255, 255) 0px);
  }
`;

export const SecondaryCta = styled(LinkWithQuery)`
  position: relative;
  display: flex;
  flex-grow: 1;
  align-items: center;
  padding: ${generateFluidPxScale(8, 14)} ${generateFluidPxScale(18, 22)};
  gap: 16px;
  white-space: nowrap;

  border: 1px dashed ${({ theme }) => theme.palette.fg1100};
  border-radius: 1px;
  font-size: ${generateFluidFontSize(16, 20, {
    minWidth: 800,
    maxWidth: 1708
  })};
  font-weight: ${({ theme }) => theme.fontWeight.NORMAL};
  font-family: inherit;
  letter-spacing: 0.25px;
  user-select: none;

  background: ${({ theme }) => darken(0.025, theme.palette.fg100)};
  color: ${({ theme }) => theme.palette.fg1200};

  box-shadow: ${({ theme }) => {
    const shadow = '0 4px 8px -1px rgba(0 0 0 / 0.64)';
    const outerGlow = `0 1px 4px 0 ${theme.palette.fgA300}`;
    return [shadow, outerGlow].join(', ');
  }};

  transition: filter 0.3s;

  &:hover {
    filter: brightness(1.13);
  }

  &::after {
    content: '';
    position: absolute;
    border-radius: inherit;
    inset: 0;
    pointer-events: none;
    background-image: ${({ theme }) => theme.textures.noise.img};
    background-size: ${({ theme }) => theme.textures.noise.size};
    mix-blend-mode: ${({ theme }) => theme.textures.noise.mixBlendMode};
    opacity: ${({ theme }) => theme.textures.noise.opacity};
  }
`;

const Circle = styled.div`
  position: absolute;
  border: 1px solid ${({ theme }) => theme.palette.fg1000};
  aspect-ratio: 1 / 1;
  width: 14px;
  border-radius: 999px;
  transition: scale 0.3s cubic-bezier(1, -0.4, 0.35, 0.95);

  &[data-position='tl'] {
    top: -6px;
    left: -6px;
    transform-origin: bottom right;
  }
  &[data-position='tr'] {
    top: -6px;
    right: -6px;
    transform-origin: bottom left;
  }
  &[data-position='bl'] {
    bottom: -6px;
    left: -6px;
    transform-origin: top right;
  }
  &[data-position='br'] {
    bottom: -6px;
    right: -6px;
    transform-origin: top left;
  }
`;

const CornerLine = styled.div`
  position: absolute;

  &::after {
    content: '';
    position: absolute;
    background-color: var(
      --corner-line-color,
      ${({ theme }) => theme.palette.fg1000}
    );
  }
`;

const CornerLinePosition = styled.div`
  --in-out-cubic: cubic-bezier(0.645, 0.045, 0.355, 1);
  --out-cubic: cubic-bezier(0.215, 0.61, 0.355, 1);

  position: absolute;
  inset: 0;
  opacity: var(--corner-line-initial-opacity, 0);
`;

const CornerTrack = styled.div`
  position: absolute;
  overflow: clip;

  &[data-position='t'],
  &[data-position='b'] {
    left: var(--corner-line-outset, -4px);
    height: 1px;
    width: calc(100% + var(--corner-line-outset, -4px) * -2);

    ${CornerLine} {
      height: 1px;
      width: 100%;

      &::after {
        height: 1px;
        width: 14px;
        transform: translateX(-50%);
      }
    }
  }

  &[data-position='r'],
  &[data-position='l'] {
    top: var(--corner-line-outset, -4px);
    width: 1px;
    height: calc(100% + var(--corner-line-outset, -4px) * -2);

    ${CornerLine} {
      width: 1px;
      height: 100%;

      &::after {
        width: 1px;
        height: 14px;
        transform: translateY(-50%);
      }
    }
  }

  &[data-position='t'] {
    top: var(--corner-line-outset, -4px);

    ${CornerLine}[data-order='1'] {
      transform: translateX(-100%);
    }
    ${CornerLine}[data-order='2'] {
      transform: translateX(0%);
    }
    ${CornerLine}[data-order='3'] {
      transform: translateX(100%);
    }
  }
  &[data-position='r'] {
    right: var(--corner-line-outset, -4px);

    ${CornerLine}[data-order='1'] {
      transform: translateY(-100%);
    }
    ${CornerLine}[data-order='2'] {
      transform: translateY(0%);
    }
    ${CornerLine}[data-order='3'] {
      transform: translateY(100%);
    }
  }
  &[data-position='b'] {
    bottom: var(--corner-line-outset, -4px);

    ${CornerLine}[data-order='1'] {
      transform: translateX(0%);
    }
    ${CornerLine}[data-order='2'] {
      transform: translateX(100%);
    }
    ${CornerLine}[data-order='3'] {
      transform: translateX(200%);
    }
  }
  &[data-position='l'] {
    left: var(--corner-line-outset, -4px);

    ${CornerLine}[data-order='1'] {
      transform: translateY(0%);
    }
    ${CornerLine}[data-order='2'] {
      transform: translateY(100%);
    }
    ${CornerLine}[data-order='3'] {
      transform: translateY(200%);
    }
  }
`;

const AccentsContainer = styled.div`
  position: relative;
  display: flex;

  & > * {
    flex-grow: 1;
  }

  ${Circle} {
    transition-delay: 0;
  }
  @media (hover: hover) {
    &:hover ${Circle} {
      transition-delay: calc(var(--i, 0) * 0.04s);
      scale: 0;
    }
  }

  ${CornerLinePosition} {
    transition:
      transform 0s var(--in-out-cubic),
      opacity 0.4s var(--out-cubic);
    transition-delay: 0s;
  }
  @media (hover: hover) {
    &:hover ${CornerLinePosition} {
      transition:
        transform 0.4s var(--out-cubic),
        opacity 0.4s var(--out-cubic);

      opacity: 1;

      &[data-position='t'] {
        transform: translateX(100%);
      }
      &[data-position='r'] {
        transform: translateY(100%);
      }
      &[data-position='b'] {
        transform: translateX(-100%);
      }
      &[data-position='l'] {
        transform: translateY(-100%);
      }
    }
  }
`;

export function CircleAccents({ children }: { children: ReactNode }) {
  return (
    <AccentsContainer>
      {['tl', 'tr', 'bl', 'br'].map((position, i) => (
        <Circle key={position} data-position={position} style={{ '--i': i }} />
      ))}
      {children}
    </AccentsContainer>
  );
}

export function CornerAccents({
  children,
  style,
  className
}: { children: ReactNode } & Pick<
  JSX.IntrinsicElements['div'],
  'style' | 'className'
>) {
  return (
    <AccentsContainer className={className} style={style}>
      {['t', 'r', 'b', 'l'].map((position, i) => (
        <CornerTrack
          key={position}
          data-position={position}
          style={{ '--i': i }}>
          <CornerLinePosition data-position={position}>
            {[1, 2, 3].map(order => (
              <CornerLine key={order} data-order={order} />
            ))}
          </CornerLinePosition>
        </CornerTrack>
      ))}
      {children}
    </AccentsContainer>
  );
}

const GlowContainer = styled.div`
  position: relative;
  display: flex;

  & > * {
    flex-grow: 1;
  }
`;

const GlowCorners = styled.div`
  position: absolute;
  width: 100%;
  height: 6px;
  top: 4px;

  &::before,
  &::after {
    position: absolute;
    content: '';
    background-color: ${({ theme }) => theme.palette.fg1200};
    background-color: ${({ theme }) => theme.palette.fg1200};
    height: 6px;
    width: 6px;
    left: 4px;
  }

  &::after {
    left: auto;
    right: 4px;
  }

  &[data-bottom] {
    top: auto;
    bottom: 4px;
  }
`;

export function InnerCornerGlow({ children }: { children: ReactNode }) {
  return (
    <GlowContainer>
      <GlowCorners />
      <GlowCorners data-bottom />
      {children}
    </GlowContainer>
  );
}

const PerspectiveWrapper = styled.div`
  opacity: 0.99;
  will-change: transform;
  display: flex;
  justify-self: stretch;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: -0px;
    top: 0px;
    height: 3.5px;
    width: 3.5px;
    border-radius: 0px;
    transform: rotate(35deg) translate(100%);
    backface-visibility: visible;
    transform-origin: top left;
    background-color: ${({ theme }) =>
      saturate(0.0, lighten(0.12, theme.palette.fg900))};
  }

  &::after {
    left: auto;
    top: auto;
    right: 0.5px;
    bottom: 0px;
    height: 2.5px;
    width: 2.5px;
    transform: rotate(35deg) translateY(0%) translateX(-100%);
    transform-origin: bottom right;
    background-color: ${({ theme }) =>
      saturate(0.0, lighten(0.1, theme.palette.fg1000))};
  }

  & > * {
    position: relative;
    z-index: 1;
  }
`;

const Depth = styled.div`
  position: absolute;
  inset: 0;
  transform: translateZ(-0.01px);

  &::before {
    content: '';
    position: absolute;
    inset: 0 2px;
    border-radius: 2px;
    background-color: rgba(0 0 0 / 0.16);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 1px;
    border-radius: 2px;
    background-color: ${({ theme }) =>
      saturate(0.0, darken(0.06, theme.palette.fg1200))};
    background-image: ${({ theme }) => {
      const stop1 = saturate(0.0, lighten(0.12, theme.palette.fg900));
      const stop2 = saturate(0.0, darken(0.08, theme.palette.fg1200));
      const stop3 = saturate(0.0, lighten(0.1, theme.palette.fg1000));
      return `linear-gradient(168deg, ${stop1}, ${stop2}, ${stop3})`;
    }};
  }
`;

const PerspectiveContainer = styled.div`
  --out-quint: cubic-bezier(0.23, 1, 0.32, 1);
  --overshoot-out: cubic-bezier(0.175, 0.885, 0.32, 1.275);
  --anticipate-out: cubic-bezier(1, -0.4, 0.35, 0.95);
  --quick-out: cubic-bezier(0, 0, 0.2, 1);
  --swift-out: cubic-bezier(0.175, 0.885, 0.32, 1.1);

  position: relative;
  display: flex;
  justify-content: stretch;
  align-items: stretch;
  transform-origin: top center;
  perspective: 300px;
  transform-style: preserve-3d;

  ${Depth} {
    &::before,
    &::after {
      transition: 0.4s transform var(--swift-out);
    }
  }

  &, ${PerspectiveWrapper} {
    &,
    &::before,
    &::after {
      transition:
        0.4s transform var(--swift-out),
        0.24s filter ease-in;
    }
  }

  @media (hover: hover) {

    &:hover {
      transform: rotateX(-16deg) rotateY(-4deg);
    }
  
    &:hover ${Depth}::before {
      transform: translateY(6px) translateX(-7px);
    }
  
    &:hover ${Depth}::after {
      transform: translateY(3px) translateX(-2px);
    }
  
    &:hover ${PerspectiveWrapper} {
      filter: brightness(1.06);
      transform: translateY(-1px) translateX(1px);
  
      &::before {
        transform: rotate(35deg) translate(0%);
      }
  
      &::after {
        transform: rotate(35deg) translateY(100%);};
      }
    }
  }
`;

export function PrimaryCtaPerspective({ children }: { children: ReactNode }) {
  return (
    <PerspectiveContainer>
      <Depth />
      <PerspectiveWrapper>{children}</PerspectiveWrapper>
    </PerspectiveContainer>
  );
}
